import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Rocket, CreditCard, Paintbrush, Download, Sparkles,
  ChevronRight, ChevronLeft, SkipForward, Check, X,
  Building2, Target, Palette, Plus, Search, ArrowRight, Zap
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'welcome', icon: Rocket, title: 'Benvenuto', subtitle: 'Configura il tuo profilo' },
  { id: 'plan', icon: CreditCard, title: 'Scegli Piano', subtitle: 'Seleziona la licenza' },
  { id: 'whitelabel', icon: Paintbrush, title: 'Personalizza', subtitle: 'Il tuo branding' },
  { id: 'plugin', icon: Download, title: 'Plugin WP', subtitle: 'Scarica e installa' },
  { id: 'ai_demo', icon: Sparkles, title: 'Prova AI', subtitle: 'La tua prima analisi' }
];

// ── Step 1: Welcome ──
const StepWelcome = ({ config, setConfig, user }) => (
  <div className="space-y-8" data-testid="onboarding-step-welcome">
    <div className="text-center">
      <div className="w-20 h-20 rounded-2xl bg-[#0055FF]/10 flex items-center justify-center mx-auto mb-6">
        <Rocket className="w-10 h-10 text-[#0055FF]" />
      </div>
      <h2 className="font-['Outfit'] text-3xl font-medium mb-3">
        Benvenuto in AEO Orchestra!
      </h2>
      <p className="text-[#A1A1AA] max-w-md mx-auto">
        Configuriamo insieme il tuo ambiente SEO in pochi minuti.
      </p>
    </div>
    <div className="max-w-sm mx-auto space-y-4">
      <div>
        <label className="text-xs text-[#A1A1AA] block mb-1.5">Nome Azienda</label>
        <Input
          value={config.company_name}
          onChange={(e) => setConfig(c => ({ ...c, company_name: e.target.value }))}
          placeholder={user?.company_name || 'La tua azienda'}
          className="bg-[#0a0a0a] border-white/10 text-white"
          data-testid="onboarding-company-name"
        />
      </div>
      <div>
        <label className="text-xs text-[#A1A1AA] block mb-1.5">Settore</label>
        <select
          value={config.sector}
          onChange={(e) => setConfig(c => ({ ...c, sector: e.target.value }))}
          className="w-full bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
          data-testid="onboarding-sector"
        >
          <option value="">Seleziona...</option>
          <option value="ecommerce">E-commerce</option>
          <option value="saas">SaaS / Software</option>
          <option value="agency">Agenzia Web / Marketing</option>
          <option value="healthcare">Salute / Benessere</option>
          <option value="real_estate">Immobiliare</option>
          <option value="restaurant">Ristorazione / Food</option>
          <option value="education">Educazione / Formazione</option>
          <option value="finance">Finanza / Assicurazioni</option>
          <option value="legal">Legale</option>
          <option value="travel">Turismo / Viaggi</option>
          <option value="automotive">Automotive</option>
          <option value="other">Altro</option>
        </select>
      </div>
    </div>
  </div>
);

// ── Step 2: Plan Selection ──
const StepPlan = ({ onSelectPlan, hasLicense }) => {
  const plans = [
    { id: 'starter', name: 'Starter', price: '79', desc: '1 sito, 300 crediti', features: ['1 sito WordPress', 'Specialisti AI, perfettamente orchestrati', '300 crediti inclusi'] },
    { id: 'professional', name: 'Professional', price: '129', desc: '3 siti, 500 crediti', popular: true, features: ['3 siti WordPress', 'Specialisti AI, perfettamente orchestrati', '500 crediti inclusi', 'Priority support'] },
    { id: 'b2b_custom', name: 'B2B White-Label', price: '3000', desc: 'Setup completo', features: ['Setup completo', 'Branding personalizzato', 'Supporto dedicato'] }
  ];

  return (
    <div data-testid="onboarding-step-plan">
      <div className="text-center mb-8">
        <h2 className="font-['Outfit'] text-2xl font-medium mb-2">Scegli il tuo piano</h2>
        <p className="text-[#A1A1AA] text-sm">
          {hasLicense ? 'Hai già una licenza attiva! Puoi procedere.' : 'Seleziona la licenza per il tuo progetto.'}
        </p>
      </div>

      {/* Trial Banner */}
      <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 mb-6 text-center" data-testid="trial-banner">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-[#10B981]" />
          <span className="text-sm font-medium text-[#10B981]">Prova gratuita attiva — 7 giorni + 50 crediti AI</span>
        </div>
        <p className="text-xs text-[#A1A1AA]">Stai usando il piano trial. Ogni analisi SEO costa 2 crediti. Puoi fare ~25 analisi gratis!</p>
      </div>

      {hasLicense && (
        <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 mb-6 text-center">
          <Check className="w-5 h-5 text-[#10B981] inline mr-2" />
          <span className="text-sm text-[#10B981]">Hai già una licenza attiva</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map(plan => (
          <button
            key={plan.id}
            onClick={() => onSelectPlan(plan.id)}
            className={`text-left p-5 rounded-2xl border transition-all hover:scale-[1.02] ${
              plan.popular ? 'bg-[#0055FF]/5 border-[#0055FF]/40' : 'bg-[#111111] border-white/10 hover:border-white/20'
            }`}
            data-testid={`onboarding-plan-${plan.id}`}
          >
            {plan.popular && <span className="text-[10px] uppercase tracking-wider text-[#0055FF] font-semibold">Consigliato</span>}
            <h3 className="font-['Outfit'] font-medium text-lg mt-1">{plan.name}</h3>
            <div className="flex items-baseline gap-1 my-2">
              <span className="text-2xl font-['Outfit'] font-semibold">{plan.price}</span>
              <span className="text-[#A1A1AA] text-sm">EUR</span>
            </div>
            <ul className="space-y-1.5 mt-3">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-[#A1A1AA]">
                  <Check className="w-3 h-3 text-[#10B981] flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Step 3: White-Label ──
const StepWhiteLabel = ({ config, setConfig }) => {
  const [newKw, setNewKw] = useState('');
  const addKw = () => {
    const k = newKw.trim();
    if (k && !config.target_keywords.includes(k)) {
      setConfig(c => ({ ...c, target_keywords: [...c.target_keywords, k] }));
      setNewKw('');
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-step-whitelabel">
      <div className="text-center mb-4">
        <h2 className="font-['Outfit'] text-2xl font-medium mb-2">Personalizza il branding</h2>
        <p className="text-[#A1A1AA] text-sm">Questi colori verranno applicati al tuo plugin WordPress</p>
      </div>

      {/* Live Mini-Preview */}
      <div className="rounded-xl overflow-hidden border border-white/10 max-w-sm mx-auto">
        <div className="p-4" style={{ background: `linear-gradient(135deg, ${config.primary_color}, ${config.secondary_color})` }}>
          <span className="text-white font-bold text-sm">{config.company_name || 'Il tuo Brand'}</span>
        </div>
        <div className="bg-white p-3 flex gap-2">
          {['A', 'M', 'C'].map((l, i) => (
            <div key={i} className="flex-1 h-6 rounded" style={{ backgroundColor: [config.primary_color, config.accent_color, config.secondary_color][i] + '25' }} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-lg mx-auto">
        <div className="space-y-3">
          <span className="text-xs text-[#A1A1AA] flex items-center gap-1"><Palette className="w-3 h-3" /> Colori</span>
          {[
            { label: 'Primario', field: 'primary_color' },
            { label: 'Secondario', field: 'secondary_color' },
            { label: 'Accento', field: 'accent_color' }
          ].map(({ label, field }) => (
            <div key={field} className="flex items-center gap-2">
              <input
                type="color"
                value={config[field]}
                onChange={(e) => setConfig(c => ({ ...c, [field]: e.target.value }))}
                className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent"
                data-testid={`onboarding-${field}`}
              />
              <span className="text-xs text-[#A1A1AA] flex-1">{label}</span>
              <span className="text-xs font-mono text-white/50">{config[field]}</span>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <span className="text-xs text-[#A1A1AA] flex items-center gap-1"><Target className="w-3 h-3" /> Keywords</span>
          <p className="text-[10px] text-[#A1A1AA] leading-relaxed">
            Inserisci le keyword principali del tuo business. L'AI le userà per ottimizzare analisi SEO, meta tag e contenuti generati per il tuo sito.
          </p>
          <div className="flex gap-1">
            <Input
              value={newKw}
              onChange={(e) => setNewKw(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKw())}
              placeholder="Keyword..."
              className="bg-[#0a0a0a] border-white/10 text-white text-xs h-8"
              data-testid="onboarding-keyword-input"
            />
            <Button onClick={addKw} size="sm" variant="outline" className="border-white/10 h-8 w-8 p-0">
              <Plus className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {config.target_keywords.map((kw, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]"
                style={{ backgroundColor: config.primary_color + '20', color: config.primary_color }}>
                {kw}
                <button onClick={() => setConfig(c => ({ ...c, target_keywords: c.target_keywords.filter(k => k !== kw) }))} className="hover:opacity-60">
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Step 4: Plugin Download ──
const StepPlugin = ({ config, token, hasLicense }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const downloadPlugin = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/plugin/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const slug = config.company_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'seo-aeo-orchestra';
      a.download = `${slug}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDownloaded(true);
    } catch {
      alert('Licenza attiva richiesta per scaricare il plugin.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="text-center space-y-6" data-testid="onboarding-step-plugin">
      <div className="w-20 h-20 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mx-auto">
        <Download className="w-10 h-10 text-[#10B981]" />
      </div>
      <h2 className="font-['Outfit'] text-2xl font-medium">Scarica il Plugin</h2>
      <p className="text-[#A1A1AA] text-sm max-w-md mx-auto">
        Il tuo plugin personalizzato "{config.company_name || 'AEO Orchestra'}" è pronto per il download.
      </p>

      <div className="bg-[#0a0a0a] rounded-xl p-6 max-w-md mx-auto border border-white/5">
        <div className="space-y-3 text-left text-sm">
          <div className="flex justify-between">
            <span className="text-[#A1A1AA]">Brand:</span>
            <span className="font-medium">{config.company_name || 'AEO Orchestra'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#A1A1AA]">Colore Primario:</span>
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 rounded" style={{ backgroundColor: config.primary_color }} />
              <span className="font-mono text-xs">{config.primary_color}</span>
            </span>
          </div>
          {config.sector && (
            <div className="flex justify-between">
              <span className="text-[#A1A1AA]">Settore:</span>
              <span className="font-medium capitalize">{config.sector.replace('_', ' ')}</span>
            </div>
          )}
          {config.target_keywords.length > 0 && (
            <div className="flex justify-between">
              <span className="text-[#A1A1AA]">Keywords:</span>
              <span className="font-medium">{config.target_keywords.length}</span>
            </div>
          )}
        </div>
      </div>

      {hasLicense ? (
        <Button
          onClick={downloadPlugin}
          disabled={downloading}
          className={`px-8 py-5 rounded-xl text-white ${downloaded ? 'bg-[#10B981]' : 'bg-[#10B981] hover:bg-[#0D9668]'}`}
          data-testid="onboarding-download-btn"
        >
          {downloading ? 'Preparazione...' : downloaded ? (
            <><Check className="w-4 h-4 mr-2" /> Scaricato!</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> Scarica Plugin .zip</>
          )}
        </Button>
      ) : (
        <p className="text-[#A1A1AA] text-sm">
          Acquista una licenza per scaricare il plugin personalizzato.
        </p>
      )}

      <div className="text-left bg-[#111111] rounded-xl p-5 max-w-md mx-auto border border-white/10">
        <h4 className="text-sm font-medium mb-3">Istruzioni di installazione:</h4>
        <ol className="space-y-2 text-xs text-[#A1A1AA]">
          <li className="flex gap-2"><span className="text-[#0055FF] font-bold">1.</span> Vai su WordPress &rarr; Plugin &rarr; Aggiungi nuovo</li>
          <li className="flex gap-2"><span className="text-[#0055FF] font-bold">2.</span> Clicca "Carica Plugin" e seleziona il file .zip</li>
          <li className="flex gap-2"><span className="text-[#0055FF] font-bold">3.</span> Attiva il plugin dalla lista</li>
          <li className="flex gap-2"><span className="text-[#0055FF] font-bold">4.</span> La license key è già pre-configurata!</li>
        </ol>
      </div>
    </div>
  );
};

// ── Step 5: AI Demo ──
const StepAIDemo = ({ token }) => {
  const [url, setUrl] = useState('');
  const [keyword, setKeyword] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const runAnalysis = async () => {
    if (!url || !keyword) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/ai/analyze`, {
        url, keyword, license_key: 'ONBOARDING_DEMO'
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
    } catch (err) {
      setResult({ error: true, message: err.response?.data?.detail || 'Licenza richiesta per l\'analisi AI.' });
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-step-ai-demo">
      <div className="text-center">
        <div className="w-20 h-20 rounded-2xl bg-[#F59E0B]/10 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-10 h-10 text-[#F59E0B]" />
        </div>
        <h2 className="font-['Outfit'] text-2xl font-medium mb-2">Prova l'Analisi AI</h2>
        <p className="text-[#A1A1AA] text-sm max-w-md mx-auto">
          Inserisci un URL e una keyword per vedere l'AI SEO Agent in azione.
        </p>
      </div>

      <div className="max-w-md mx-auto space-y-3">
        <div>
          <label className="text-xs text-[#A1A1AA] block mb-1">URL del sito</label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://esempio.com"
            className="bg-[#0a0a0a] border-white/10 text-white"
            data-testid="onboarding-ai-url"
          />
        </div>
        <div>
          <label className="text-xs text-[#A1A1AA] block mb-1">Keyword target</label>
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="es: consulente seo milano"
            className="bg-[#0a0a0a] border-white/10 text-white"
            data-testid="onboarding-ai-keyword"
          />
        </div>
        <Button
          onClick={runAnalysis}
          disabled={analyzing || !url || !keyword}
          className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black font-medium rounded-xl py-5"
          data-testid="onboarding-ai-analyze-btn"
        >
          {analyzing ? (
            <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" /> Analisi in corso...</>
          ) : (
            <><Search className="w-4 h-4 mr-2" /> Analizza SEO</>
          )}
        </Button>
      </div>

      {result && (
        <div className="max-w-md mx-auto">
          {result.error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-400">
              {result.message}
            </div>
          ) : (
            <div className="bg-[#111111] rounded-xl p-5 border border-white/10 space-y-4" data-testid="onboarding-ai-result">
              {result.score !== undefined && (
                <div className="text-center">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold ${
                    result.score >= 70 ? 'bg-[#10B981]/20 text-[#10B981]' : result.score >= 40 ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#EF4444]/20 text-[#EF4444]'
                  }`}>
                    {result.score}
                  </div>
                  <p className="text-xs text-[#A1A1AA] mt-2">SEO Score</p>
                </div>
              )}
              {result.suggestions && result.suggestions.length > 0 && (
                <div>
                  <h4 className="text-xs text-[#A1A1AA] mb-2">Suggerimenti:</h4>
                  <ul className="space-y-1">
                    {result.suggestions.slice(0, 3).map((s, i) => (
                      <li key={i} className="text-xs text-white/80 flex gap-2">
                        <span className="text-[#0055FF]">&bull;</span>
                        {typeof s === 'string' ? s.substring(0, 120) : (s.description || s.type || '').substring(0, 120)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.missing_keywords && result.missing_keywords.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {result.missing_keywords.slice(0, 5).map((kw, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full text-[10px] bg-[#F59E0B]/15 text-[#F59E0B]">{kw}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Wizard ──
const OnboardingWizard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [config, setConfig] = useState({
    company_name: '',
    sector: '',
    target_keywords: [],
    primary_color: '#0055FF',
    secondary_color: '#00E5FF',
    accent_color: '#10B981'
  });
  const [hasLicense, setHasLicense] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const init = async () => {
      try {
        // Check onboarding status
        const statusRes = await axios.get(`${API_URL}/api/onboarding/status`, authHeaders);
        if (statusRes.data.completed) {
          navigate('/dashboard');
          return;
        }
        // Find which step to resume
        const stepOrder = STEPS.map(s => s.id);
        const steps = statusRes.data.steps || {};
        let resumeIdx = 0;
        for (let i = 0; i < stepOrder.length; i++) {
          if (steps[stepOrder[i]]?.completed || steps[stepOrder[i]]?.skipped) {
            resumeIdx = i + 1;
          } else {
            break;
          }
        }
        if (resumeIdx >= stepOrder.length) resumeIdx = 0;
        setCurrentStep(resumeIdx);

        // Fetch existing white-label config
        const wlRes = await axios.get(`${API_URL}/api/whitelabel/config`, authHeaders);
        setConfig({
          company_name: wlRes.data.company_name || user?.company_name || '',
          sector: wlRes.data.sector || '',
          target_keywords: wlRes.data.target_keywords || [],
          primary_color: wlRes.data.primary_color || '#0055FF',
          secondary_color: wlRes.data.secondary_color || '#00E5FF',
          accent_color: wlRes.data.accent_color || '#10B981'
        });

        // Check licenses
        const licRes = await axios.get(`${API_URL}/api/licenses`, authHeaders);
        setHasLicense(licRes.data.length > 0);
      } catch (err) {
        console.error('Failed to init onboarding:', err);
      } finally {
        setLoadingStatus(false);
      }
    };
    init();
  }, []);

  const completeStep = async (stepId) => {
    try {
      await axios.post(`${API_URL}/api/onboarding/step`, { step: stepId, completed: true }, authHeaders);
    } catch (err) {
      console.error('Failed to update step:', err);
    }
  };

  const skipStep = async (stepId) => {
    try {
      await axios.post(`${API_URL}/api/onboarding/step`, { step: stepId, skipped: true }, authHeaders);
    } catch (err) {
      console.error('Failed to skip step:', err);
    }
  };

  const handleNext = async () => {
    const step = STEPS[currentStep];

    // Save white-label on welcome or whitelabel steps
    if (step.id === 'welcome' || step.id === 'whitelabel') {
      try {
        await axios.post(`${API_URL}/api/whitelabel/config`, config, authHeaders);
      } catch (err) {
        console.error('Failed to save config:', err);
      }
    }

    // If plan step, redirect to pricing first
    if (step.id === 'plan' && !hasLicense) {
      await completeStep(step.id);
      navigate('/pricing');
      return;
    }

    await completeStep(step.id);

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSkip = async () => {
    await skipStep(STEPS[currentStep].id);
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSkipAll = async () => {
    try {
      await axios.post(`${API_URL}/api/onboarding/skip`, {}, authHeaders);
    } catch {}
    navigate('/dashboard');
  };

  const handleSelectPlan = async (planId) => {
    await completeStep('plan');
    try {
      const endpoint = ['subscription_basic', 'subscription_pro'].includes(planId) ? 'subscription' : 'create';
      const res = await axios.post(`${API_URL}/api/checkout/${endpoint}`, {
        package_id: planId,
        origin_url: window.location.origin,
        email: user.email
      }, authHeaders);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
    }
  };

  if (loadingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="onboarding-wizard">
      {/* Top Bar */}
      <div className="border-b border-white/5 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#0055FF]" />
            <span className="font-['Outfit'] font-semibold text-sm">Orchestra <span className="text-[#0055FF]">AI</span></span>
          </div>
          <button onClick={handleSkipAll} className="text-xs text-[#A1A1AA] hover:text-white flex items-center gap-1" data-testid="onboarding-skip-all">
            Salta setup <SkipForward className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-6 pt-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-1">
            {STEPS.map((step, idx) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                    idx < currentStep ? 'bg-[#10B981] text-white' :
                    idx === currentStep ? 'bg-[#0055FF] text-white ring-4 ring-[#0055FF]/20' :
                    'bg-white/5 text-[#A1A1AA]'
                  }`}>
                    {idx < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
                  </div>
                  <span className={`text-[10px] mt-1.5 hidden sm:block ${idx === currentStep ? 'text-white' : 'text-[#A1A1AA]'}`}>
                    {step.title}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`h-0.5 flex-1 mt-[-16px] sm:mt-[-4px] rounded ${idx < currentStep ? 'bg-[#10B981]' : 'bg-white/5'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          {currentStep === 0 && <StepWelcome config={config} setConfig={setConfig} user={user} />}
          {currentStep === 1 && <StepPlan onSelectPlan={handleSelectPlan} hasLicense={hasLicense} />}
          {currentStep === 2 && <StepWhiteLabel config={config} setConfig={setConfig} />}
          {currentStep === 3 && <StepPlugin config={config} token={token} hasLicense={hasLicense} />}
          {currentStep === 4 && <StepAIDemo token={token} />}
        </div>
      </div>

      {/* Bottom Nav */}
      <div className="border-t border-white/5 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
            disabled={currentStep === 0}
            className="text-[#A1A1AA] hover:text-white"
            data-testid="onboarding-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>

          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={handleSkip} className="text-[#A1A1AA] hover:text-white text-sm" data-testid="onboarding-skip-btn">
              Salta
            </Button>
            <Button onClick={handleNext} className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-xl px-6" data-testid="onboarding-next-btn">
              {currentStep === STEPS.length - 1 ? (
                <>Vai alla Dashboard <ArrowRight className="w-4 h-4 ml-1" /></>
              ) : (
                <>Avanti <ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
